import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { ExportButtons } from "@/components/reports/ExportButtons";
import { ReporteLoads } from "@/components/reports/ReporteLoads";
import { ReporteClientes } from "@/components/reports/ReporteClientes";
import { ReporteRutas } from "@/components/reports/ReporteRutas";
import { ReporteCxC } from "@/components/reports/ReporteCxC";
import { ReporteCxP } from "@/components/reports/ReporteCxP";
import { ReporteActividad } from "@/components/reports/ReporteActividad";
import { ReporteInvalidados } from "@/components/reports/ReporteInvalidados";
import { buildParams } from "@/components/reports/reportUtils";

const ALL_REPORTS = [
  { key: "loads", label: "Rentabilidad por load", path: "/reportes/rentabilidad-loads", ganancia: true,
    fields: ["company", "client", "carrier", "tipo_viaje", "dates", "estado_cobro"], Comp: ReporteLoads },
  { key: "clientes", label: "Rentabilidad por cliente", path: "/reportes/rentabilidad-clientes", ganancia: true,
    fields: ["company", "client", "tipo_viaje", "dates"], Comp: ReporteClientes },
  { key: "rutas", label: "Rentabilidad por ruta", path: "/reportes/rentabilidad-rutas", ganancia: true,
    fields: ["company", "client", "tipo_viaje", "dates"], Comp: ReporteRutas },
  { key: "cxc", label: "Cuentas por cobrar", path: "/reportes/cuentas-por-cobrar", ganancia: false,
    fields: ["company", "client", "tipo_viaje", "dates", "bucket", "tipo_fecha"], Comp: ReporteCxC },
  { key: "cxp", label: "Cuentas por pagar", path: "/reportes/cuentas-por-pagar", ganancia: false,
    fields: ["company", "carrier", "tipo_viaje", "dates", "bucket"], Comp: ReporteCxP },
  { key: "actividad", label: "Actividad por usuario", path: "/reportes/actividad-usuarios", ganancia: false,
    fields: ["dates", "user"], Comp: ReporteActividad },
  { key: "invalidados", label: "Invalidados y anulados", path: "/reportes/invalidados-anulados", ganancia: false,
    fields: ["company", "client", "carrier", "tipo_viaje", "dates"], Comp: ReporteInvalidados },
];

const EMPTY_FILTERS = {
  company_id: "all", client_id: "all", carrier_id: "all", fecha_desde: "", fecha_hasta: "",
  estado_cobro: "all", bucket: "all", tipo_fecha: "all", user_id: "all", tipo_viaje: "all",
};

export default function Reports() {
  const { can } = useAuth();
  const { companyFilter, setCompanyFilter } = usePrefs();
  const [searchParams] = useSearchParams();
  const canVerGanancia = can("reports", "ver_ganancia");
  const reports = ALL_REPORTS.filter((r) => !r.ganancia || canVerGanancia);
  const [active, setActive] = useState(() => {
    const t = searchParams.get("tab");
    return reports.some((r) => r.key === t) ? t : reports[0]?.key;
  });
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, company_id: companyFilter });
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const current = reports.find((r) => r.key === active) || reports[0];

  // Reset data when switching tabs to avoid rendering previous tab's stale shape
  useEffect(() => { setData(null); }, [active]);

  useEffect(() => {
    api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    api.get("/clients", { params: { limit: 200 } }).then(({ data }) => setClients(data.items || [])).catch(() => {});
    api.get("/carriers", { params: { limit: 200 } }).then(({ data }) => setCarriers(data.items || [])).catch(() => {});
    api.get("/audit-log/usuarios").then(({ data }) => setUsers(data || [])).catch(() => {});
  }, []);

  // Selector global de compañía en el header (Parte D) — mantiene sincronizados Reportes.
  useEffect(() => { setFilters((f) => ({ ...f, company_id: companyFilter })); }, [companyFilter]);

  const setFilter = (k, v) => {
    setFilters((f) => ({ ...f, [k]: v }));
    if (k === "company_id") setCompanyFilter(v);
  };

  const load = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    try {
      const { data } = await api.get(current.path, { params: buildParams(filters) });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [current, filters]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  if (!current) {
    return (
      <div>
        <PageHeader title="Reportes" subtitle="No tenés acceso a ningún reporte." />
      </div>
    );
  }

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reportes" subtitle="Rentabilidad, cobranza, pagos y auditoría — excluyen loads invalidados y costos anulados" />
      <Tabs value={active} onValueChange={setActive} className="mb-4">
        <TabsList className="flex h-auto flex-wrap gap-1 bg-transparent p-0" data-testid="reports-tabs">
          {reports.map((r) => (
            <TabsTrigger key={r.key} value={r.key} data-testid={`reports-tab-${r.key}`}
                        className="rounded-md border border-border bg-white data-[state=active]:border-primary data-[state=active]:bg-green-50">
              {r.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <ReportFilters filters={filters} setFilter={setFilter} companies={companies} clients={clients}
                      carriers={carriers} users={users} fields={current.fields} />
        <ExportButtons path={current.path} params={buildParams(filters)} filenameBase={current.key}
                      testIdPrefix={`reporte-${current.key}`} />
      </div>

      <div className="rounded-lg border border-border bg-white p-4">
        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="reports-loading">Cargando...</p>
        ) : (
          <current.Comp data={data} />
        )}
      </div>
    </div>
  );
}
